import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ExternalLink, RefreshCw, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getMeasurementImportPriority, updateCustomerNo } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
};

export default function MeasurementImportPriorityPage() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [customerNoEdits, setCustomerNoEdits] = useState({});
  const [savingCustomerNo, setSavingCustomerNo] = useState({});
  const [showOnlyNeeded, setShowOnlyNeeded] = useState(true);

  const loadPriority = async () => {
    try {
      setLoading(true);
      const response = await getMeasurementImportPriority();
      const data = response.data?.data || [];
      setRows(data);
      setCustomerNoEdits(
        Object.fromEntries(
          data.map((row) => [row.customer_id, row.customer_no ?? ''])
        )
      );
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load measurement priority list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPriority();
  }, []);

  const handleSaveCustomerNo = async (customerId) => {
    const rawValue = String(customerNoEdits[customerId] ?? '').trim();
    const parsedValue = rawValue === '' ? null : Number(rawValue);
    if (rawValue !== '' && (!Number.isFinite(parsedValue) || parsedValue <= 0)) {
      toast.error('Customer number must be a positive number');
      return;
    }

    try {
      setSavingCustomerNo((prev) => ({ ...prev, [customerId]: true }));
      await updateCustomerNo(customerId, parsedValue);
      setRows((prev) => prev.map((row) => (
        row.customer_id === customerId
          ? { ...row, customer_no: parsedValue, missing_customer_no: !parsedValue }
          : row
      )));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update client number');
    } finally {
      setSavingCustomerNo((prev) => ({ ...prev, [customerId]: false }));
    }
  };

  const openPerthImport = (customerId) => {
    window.open(`/fitting-studio/${customerId}?tab=perth-import-wizard`, '_blank', 'noopener,noreferrer');
  };

  const summary = useMemo(() => {
    const processing = rows.filter((row) => row.priority_group === 'processing').length;
    const completed = rows.filter((row) => row.priority_group === 'completed').length;
    const missingNumbers = rows.filter((row) => row.missing_customer_no).length;
    const missingMeasurements = rows.filter((row) => row.missing_measurements).length;
    const importedComplete = rows.filter((row) => row.is_fully_imported).length;
    const neededOnly = rows.filter((row) => row.needs_import).length;
    return { processing, completed, missingNumbers, missingMeasurements, importedComplete, neededOnly };
  }, [rows]);

  const visibleRows = useMemo(() => {
    return rows.filter((row) => {
      if (row.is_fully_imported) return false; // user preference: always hide fully imported
      if (!showOnlyNeeded) return true;
      return Boolean(row.needs_import);
    });
  }, [rows, showOnlyNeeded]);

  return (
    <div className="space-y-6 p-3" data-testid="measurement-priority-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold" data-testid="measurement-priority-title">Measurement Import Priority</h1>
          <p className="text-sm text-muted-foreground" data-testid="measurement-priority-description">
            Processing clients first, then completed. Sorted by earliest garment PDD.
          </p>
        </div>
        <Button onClick={loadPriority} variant="outline" data-testid="measurement-priority-refresh-button">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="measurement-priority-summary-row">
        <Badge variant="secondary" data-testid="measurement-priority-processing-count">Processing: {summary.processing}</Badge>
        <Badge variant="outline" data-testid="measurement-priority-completed-count">Completed: {summary.completed}</Badge>
        <Badge className="bg-emerald-100 text-emerald-700" data-testid="measurement-priority-imported-complete-count">Imported Complete: {summary.importedComplete}</Badge>
        <Badge variant="secondary" data-testid="measurement-priority-needed-only-count">Needs Import: {summary.neededOnly}</Badge>
        <Badge variant="destructive" data-testid="measurement-priority-missing-customer-no-count">Missing Client No: {summary.missingNumbers}</Badge>
        <Badge variant="outline" data-testid="measurement-priority-missing-measurements-count">Missing Measurements: {summary.missingMeasurements}</Badge>
      </div>

      <div className="flex items-center justify-between rounded border px-3 py-2" data-testid="measurement-priority-filter-bar">
        <label className="inline-flex items-center gap-2 text-sm" data-testid="measurement-priority-needed-filter-label">
          <input
            type="checkbox"
            checked={showOnlyNeeded}
            onChange={(e) => setShowOnlyNeeded(e.target.checked)}
            data-testid="measurement-priority-needed-filter-checkbox"
          />
          Show only clients needing import
        </label>
        <span className="text-xs text-muted-foreground" data-testid="measurement-priority-visible-count">
          Showing {visibleRows.length} of {rows.length}
        </span>
      </div>

      <Card data-testid="measurement-priority-table-card">
        <CardHeader>
          <CardTitle data-testid="measurement-priority-table-title">Clients needing measurement mapping</CardTitle>
          <CardDescription data-testid="measurement-priority-table-description">
            Prioritize by group and earliest PDD.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2" data-testid="measurement-priority-loading-state">
              {[...Array(6)].map((_, idx) => <Skeleton key={idx} className="h-10 w-full" />)}
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="measurement-priority-empty-state">
              No clients currently need measurement import.
            </div>
          ) : (
            <div className="overflow-x-auto rounded border" data-testid="measurement-priority-table-container">
              <table className="w-full text-sm" data-testid="measurement-priority-table">
                <thead className="bg-muted/60">
                  <tr>
                    <th className="text-left p-3">Priority Group</th>
                    <th className="text-left p-3">Earliest PDD</th>
                    <th className="text-left p-3">Client</th>
                    <th className="text-left p-3">Client No</th>
                    <th className="text-left p-3">Orders</th>
                    <th className="text-left p-3">Actions</th>
                    <th className="text-left p-3">Availability Synopsis</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr key={row.customer_id} className="border-t" data-testid={`measurement-priority-row-${row.customer_id}`}>
                      <td className="p-3">
                        <Badge variant={row.priority_group === 'processing' ? 'default' : 'secondary'} data-testid={`measurement-priority-group-${row.customer_id}`}>
                          {row.priority_group}
                        </Badge>
                      </td>
                      <td className="p-3 font-medium" data-testid={`measurement-priority-date-${row.customer_id}`}>{formatDate(row.priority_sort_date)}</td>
                      <td className="p-3" data-testid={`measurement-priority-client-${row.customer_id}`}>{row.customer_name || 'Unknown'}</td>
                      <td className="p-3" data-testid={`measurement-priority-customer-no-${row.customer_id}`}>
                        <div className="flex items-center gap-2">
                          <Input
                            value={customerNoEdits[row.customer_id] ?? ''}
                            onChange={(e) => setCustomerNoEdits((prev) => ({ ...prev, [row.customer_id]: e.target.value.replace(/[^0-9]/g, '') }))}
                            placeholder="Missing"
                            className="h-8 w-28"
                            data-testid={`measurement-priority-customer-no-input-${row.customer_id}`}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSaveCustomerNo(row.customer_id)}
                            disabled={Boolean(savingCustomerNo[row.customer_id])}
                            data-testid={`measurement-priority-customer-no-save-${row.customer_id}`}
                          >
                            <Save className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        {row.missing_customer_no && (
                          <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700" data-testid={`measurement-priority-missing-no-${row.customer_id}`}>
                            <AlertTriangle className="w-3.5 h-3.5" /> Missing client number
                          </div>
                        )}
                      </td>
                      <td className="p-3" data-testid={`measurement-priority-orders-${row.customer_id}`}>
                        <div className="space-y-1">
                          <div>Processing: {row.processing_order_count} ({(row.order_numbers_processing || []).slice(0, 3).join(', ') || '—'})</div>
                          <div className="text-muted-foreground">Completed: {row.completed_order_count} ({(row.order_numbers_completed || []).slice(0, 3).join(', ') || '—'})</div>
                        </div>
                      </td>
                      <td className="p-3" data-testid={`measurement-priority-actions-${row.customer_id}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openPerthImport(row.customer_id)}
                          data-testid={`measurement-priority-open-perth-${row.customer_id}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5 mr-1" /> Perth Import
                        </Button>
                      </td>
                      <td className="p-3" data-testid={`measurement-priority-synopsis-${row.customer_id}`}>
                        <div className="space-y-1">
                          <div>{row.availability_synopsis}</div>
                          {row.discrepancy_summary ? (
                            <Badge className="bg-amber-100 text-amber-800" data-testid={`measurement-priority-discrepancy-${row.customer_id}`}>
                              Discrepancy: {row.discrepancy_summary}
                            </Badge>
                          ) : null}
                          {row.missing_measurements && (
                            <Badge variant="destructive" data-testid={`measurement-priority-measurement-missing-${row.customer_id}`}>Measurements Missing</Badge>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
